import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { getAuthToken } from "lib/utils/auth";
import { isHttpSecure } from "lib/utils/utils";
import type { RootState } from "store";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";

const defaultHost = process.env.REACT_APP_SINGLE_ARC_HOSTNAME || window.location.hostname;
const scheme = isHttpSecure() ? "https" : "http";

const makeSchedulerQuery = (host: string, arcId?: string | null) => fetchBaseQuery({
    // Direct ARC mode uses arc_gateway; fleet mode uses the proxy path.
    baseUrl: gatewayServiceBaseUrl("missionScheduler", arcId, host) ?? `${scheme}://${host}:12404/`,
    prepareHeaders: (headers) => {
        const token = getAuthToken();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
    },
});

const _defaultQuery = makeSchedulerQuery(defaultHost);

const schedulerBaseQuery: BaseQueryFn = (args, api, extraOptions) => {
    const { arcId, arcProxyHost } = (api.getState() as RootState).arcProxy;
    const effectiveHost = (arcId && arcProxyHost) ? arcProxyHost : defaultHost;
    if (arcId && !arcProxyHost) {
        return { error: { status: "CUSTOM_ERROR", error: "Fleet proxy host is unavailable" } };
    }
    const innerQuery = !arcId && effectiveHost === defaultHost
        ? _defaultQuery
        : makeSchedulerQuery(effectiveHost, arcId);
    return innerQuery(args, api, extraOptions);
};

export interface MissionSchedule {
    id: string;
    name: string;
    mission_id: string;
    schedule_type: "once" | "repeating";
    run_at: string;
    interval_seconds: number;
    enabled: boolean;
    metadata: Record<string, unknown>;
    last_executed_at?: string;
    next_run_at?: string;
    created_at: string;
    updated_at: string;
}

export const missionSchedulerApi = createApi({
    reducerPath: "missionSchedulerApi",
    baseQuery: schedulerBaseQuery,
    tagTypes: ["Schedule"],
    endpoints: (builder) => ({
        getSchedules: builder.query<{ schedules: MissionSchedule[] }, void>({
            query: () => "schedules",
            providesTags: (result) =>
                result
                    ? [
                          ...result.schedules.map(({ id }) => ({ type: "Schedule" as const, id })),
                          { type: "Schedule" as const, id: "LIST" },
                      ]
                    : [{ type: "Schedule" as const, id: "LIST" }],
        }),
        createSchedule: builder.mutation<
            { schedule: MissionSchedule },
            {
                name: string;
                mission_id: string;
                schedule_type: "once" | "repeating";
                run_at?: string;
                interval_seconds?: number;
                metadata?: Record<string, unknown>;
                enabled?: boolean;
            }
        >({
            query: (body) => ({
                url: "schedules",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "Schedule", id: "LIST" }],
        }),
        updateSchedule: builder.mutation<
            { schedule: MissionSchedule },
            {
                id: string;
                name?: string;
                mission_id?: string;
                schedule_type?: "once" | "repeating";
                run_at?: string;
                interval_seconds?: number;
                metadata?: Record<string, unknown>;
                enabled?: boolean;
            }
        >({
            query: ({ id, ...body }) => ({
                url: `schedules/${id}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: "Schedule", id: arg.id },
                { type: "Schedule", id: "LIST" },
            ],
        }),
        deleteSchedule: builder.mutation<{ deleted: boolean; id: string }, string>({
            query: (id) => ({
                url: `schedules/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: "Schedule", id },
                { type: "Schedule", id: "LIST" },
            ],
        }),
        executeSchedule: builder.mutation<
            {
                schedule_id: string;
                mission_id: string;
                success: boolean;
                queued?: boolean;
                run_at?: string;
                seconds_until_run?: number;
                return_code: number;
                stdout: string;
                stderr: string;
                message?: string;
            },
            string
        >({
            query: (id) => ({
                url: `schedules/${id}/execute`,
                method: "POST",
            }),
        }),
    }),
});

export const {
    useGetSchedulesQuery,
    useCreateScheduleMutation,
    useUpdateScheduleMutation,
    useDeleteScheduleMutation,
    useExecuteScheduleMutation,
} = missionSchedulerApi;
